import { useState, useContext } from 'react';
import {
    Container,
    Box,
    Typography,
    Grid,
    CircularProgress,
} from '@mui/material';
import {
    CreditCard,
    PostAdd,
    AccountBalanceWallet,
    Contactless,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import {
    AppContext,
    SttButton,
    SttLabelTitle,
    SttLabelWeekdays,
    SttPage,
} from '../../../sporttia/all';
import { getPath } from '../../Pages';
import {
    normalizeDuration,
    formatPriceByLocale,
    getErrorMessage,
} from '../../../lib/utils';
import constants from '../../../config/constants';
import useBonosService from '../../../services/BonosService';
import SttError from '../../../components/error/SttError';
import useScsService from '../../../services/ScsService';
import ModalConfirmation from '../../../layout/ModalConfirmation';
import { PaymentForm } from '../../../types/payment';

export default function BuyBono() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, idSC } = useParams<{ id: string; idSC: string }>();

    const bonoId = id ? parseInt(id, 10) : 0;
    const scId = idSC ? parseInt(idSC, 10) : 0;

    const [selectedPaymentForm, setSelectedPaymentForm] =
        useState<PaymentForm>();

    const bonosService = useBonosService();
    const scsService = useScsService();

    const getBonoQuery = bonosService.useGetBono(bonoId);
    const getScMeQuery = scsService.useGetScMe(scId);
    const getScTpvConfs = scsService.useGetScTpvConfs(scId);
    const createBonoUserMutation = bonosService.useCreateBonoUser({
        onSuccess: (response) => {
            if (response.tpv) {
                navigate(getPath('tpvSwitcher', { id: response.tpv.id }));
            } else if (response.boleto) {
                navigate(getPath('scProfile', { id: idSC }));
            }
        },
        onError: (error) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    if (!bonoId || !scId) {
        return <SttError />;
    }

    if (
        getBonoQuery.isLoading ||
        getScMeQuery.isLoading ||
        getScTpvConfs.isLoading
    ) {
        return (
            <Container maxWidth="md">
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (getBonoQuery.isError || getScMeQuery.isError || getScTpvConfs.isError) {
        return <SttError />;
    }

    if (!getBonoQuery.data || !getScMeQuery.data || !getScTpvConfs.data) {
        return null;
    }

    const { bono } = getBonoQuery.data;
    const { mship } = getScMeQuery.data;

    const hasZitycard = getScTpvConfs.data.rows.some(
        (tpv) => tpv.bank === constants.tpvConf.banks.zitycard.name,
    );

    return (
        <Container maxWidth="md">
            <SttPage innerTitle={t('bono.buyBono')}>
                {mship && <SttLabelTitle>{mship.sc!.name}</SttLabelTitle>}
                <Box mt={2}>
                    <Box className="tw-space-y-2">
                        <div className="tw-text-sm">
                            <span className="tw-text-gray-600">
                                {t('labels.name')}:
                            </span>{' '}
                            <b className="tw-font-semibold tw-text-gray-900">
                                {bono.name}
                            </b>
                        </div>
                        <div className="tw-text-sm">
                            <span className="tw-text-gray-600">
                                {t('bono.numConsumed')}:
                            </span>{' '}
                            <b className="tw-font-semibold tw-text-gray-900">
                                {bono.amount} x{' '}
                                {bono.duration
                                    ? normalizeDuration(bono.duration)
                                    : ''}
                            </b>
                        </div>
                        <div className="tw-text-sm">
                            <span className="tw-text-gray-600">
                                {t('bono.priceBono')}:
                            </span>{' '}
                            <b className="tw-font-semibold tw-text-gray-900">
                                {formatPriceByLocale(
                                    bono?.price,
                                    bono?.priceShape?.currency ?? undefined,
                                )}
                            </b>
                        </div>
                        {bono.expiration && (
                            <div className="tw-text-sm">
                                <span className="tw-text-gray-600">
                                    {t('bono.expiration')}:
                                </span>{' '}
                                <b className="tw-font-semibold tw-text-gray-900">
                                    {bono.expirationLang}
                                </b>
                            </div>
                        )}
                        {bono.dateend && (
                            <div className="tw-text-sm">
                                <span className="tw-text-gray-600">
                                    {t('bono.validUntil')}:
                                </span>{' '}
                                <b className="tw-font-semibold tw-text-gray-900">
                                    {format(
                                        parseISO(bono.dateend),
                                        'dd/MM/yyyy',
                                        {
                                            locale: getDateLocale(),
                                        },
                                    )}
                                </b>
                            </div>
                        )}
                        <Box mt={1} className="tw-text-sm">
                            <b className="tw-font-semibold tw-text-gray-900">
                                {t('bono.horary')}:
                            </b>
                            <SttLabelWeekdays weekdays={bono.weekdays} />
                            <span className="tw-text-gray-700">{`${bono.timeini} - ${bono.timeend}`}</span>
                        </Box>
                        <Box mt={1}>
                            <div className="tw-text-sm">
                                <span className="tw-text-gray-600">
                                    {t('labels.type')}:
                                </span>{' '}
                                <b className="tw-font-semibold tw-text-gray-900">
                                    {bono.typeLang}
                                </b>
                            </div>
                            <Typography variant="caption">
                                * {t('bono.typeBono')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box mt={2}>{bono.description}</Box>

                    <Box mt={2}>
                        {bono.facilities && bono.facilities.length > 0 && (
                            <div className="tw-space-y-2">
                                <b className="tw-font-semibold tw-text-gray-900">
                                    {t('bono.facilities')}:
                                </b>
                                <Box pl={1} pt={1} className="tw-space-y-1">
                                    {bono.facilities.map((facility) => (
                                        <div
                                            key={facility.id}
                                            className="tw-text-sm tw-text-gray-700"
                                        >
                                            {facility.name}
                                        </div>
                                    ))}
                                </Box>
                            </div>
                        )}
                    </Box>

                    <Box mt={3}>
                        <Grid container spacing={3}>
                            {/* --- Paying with TPV --- */}
                            {bono.price !== null &&
                                bono.price !== undefined &&
                                bono.price > 0 && (
                                    <>
                                        {mship &&
                                            bono.purchasePFs?.includes(
                                                'PURSE',
                                            ) && (
                                                <Grid
                                                    size={{
                                                        xs: 12,
                                                        sm: 12,
                                                        md: 6,
                                                    }}
                                                >
                                                    <SttButton
                                                        fullWidth
                                                        variant="contained"
                                                        color="primary"
                                                        caption={`${t(
                                                            'payment.payWithPurse',
                                                        )} (${formatPriceByLocale(
                                                            mship?.amount,
                                                            mship?.amountShape
                                                                ?.currency ??
                                                                undefined,
                                                        )})`}
                                                        startIcon={
                                                            <AccountBalanceWallet />
                                                        }
                                                        onClick={() => {
                                                            setSelectedPaymentForm(
                                                                constants
                                                                    .payment
                                                                    .paymentForms
                                                                    .purse.name,
                                                            );
                                                        }}
                                                    />
                                                </Grid>
                                            )}

                                        {bono.purchasePFs?.includes('TPV') && (
                                            <Grid
                                                size={{ xs: 12, sm: 12, md: 6 }}
                                            >
                                                <SttButton
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    caption={t(
                                                        'tpv.payWithTPV',
                                                    )}
                                                    startIcon={<CreditCard />}
                                                    onClick={() => {
                                                        setSelectedPaymentForm(
                                                            constants.payment
                                                                .paymentForms
                                                                .tpv.name,
                                                        );
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                    </>
                                )}

                            {!bono.price && (
                                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                                    <SttButton
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        caption={t('abono.getAbono')}
                                        startIcon={<PostAdd />}
                                        onClick={() => {
                                            setSelectedPaymentForm(
                                                constants.payment.paymentForms
                                                    .free.name,
                                            );
                                        }}
                                    />
                                </Grid>
                            )}

                            {hasZitycard && (
                                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                                    <SttButton
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        caption={t('payment.payWithZitycard')}
                                        startIcon={<Contactless />}
                                        onClick={() => {
                                            setSelectedPaymentForm(
                                                constants.payment.paymentForms
                                                    .zitycard.name,
                                            );
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>
            </SttPage>

            {selectedPaymentForm && (
                <ModalConfirmation
                    show
                    onAccept={() => {
                        createBonoUserMutation.mutate(
                            {
                                userId: cxt.user!.id!,
                                bonoId: bono.id,
                                params: {
                                    paymentForm: selectedPaymentForm,
                                },
                            },
                            {
                                onSettled: () => {
                                    setSelectedPaymentForm(undefined);
                                },
                            },
                        );
                    }}
                    onClose={() => {
                        setSelectedPaymentForm(undefined);
                    }}
                />
            )}
        </Container>
    );
}
